import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

interface AdminCollapsibleSectionProps {
  id: string;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
}

const AdminCollapsibleSection = ({
  id,
  title,
  subtitle,
  icon,
  defaultExpanded = false,
  children,
}: AdminCollapsibleSectionProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <section className="rounded-xl sm:rounded-2xl border border-gray-200/80 bg-white shadow-sm shadow-black/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-controls={`admin-section-panel-${id}`}
        className="w-full flex items-center gap-3 sm:gap-4 p-4 sm:p-5 text-left hover:bg-gray-50/80 transition-colors touch-manipulation min-h-[56px]"
      >
        {icon && (
          <div className="flex-shrink-0 [&>div]:!m-0">{icon}</div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-gray-800 truncate">{title}</h3>
          {subtitle && (
            <p className="text-xs sm:text-sm text-gray-500 truncate mt-0.5">{subtitle}</p>
          )}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      <div
        id={`admin-section-panel-${id}`}
        hidden={!expanded}
        className="border-t border-gray-100"
      >
        {children}
      </div>
    </section>
  );
};

export default AdminCollapsibleSection;
